import { Vector3 } from 'three/webgpu';
import { buildEntrances, entranceOptions } from '../../engine/caves/entrance';
import { caveGap, MIN_GAP } from '../../engine/caves/gap';
import { hashSeed, mulberry32 } from '../../engine/math/rng';
import type { TerrainProfile } from '../../engine/terrain/TerrainProfile';
import { type GroundSample, TerrainHeights } from '../../engine/terrain/terrainHeight';
import { makeHollow } from '../../engine/universe/HollowDef';
import { perpendicularBasis, type SpiralOptions } from '../../engine/universe/Tunnel';
import { plateauSize } from '../scenes/cavePlacement';

/*
 * Where a custom universe's worlds sit and where their caves open, found before anything is
 * built, from the terrain's heights on the CPU (terrainHeight.ts). Each cave's direction is
 * searched so both mouths open on dry, gentle ground, no world overlaps another and no cave
 * passes through a world it doesn't join.
 */

/** A world as placement sees it. */
export interface PlaceWorld {
  radius: number;
  seed: number;
  terrain: TerrainProfile;
  /**
   * Lowest ground (m) for a mouth or the start at unit direction `dir` (hollow frame): above
   * the water, out of the lava's glow (it depends on the biome there).
   */
  dry: (dir: Vector3) => number;
}

/** A cave to place: `to` is a world not yet placed, `from` one already placed. */
export interface PlaceLink {
  from: number;
  to: number;
  /** Rock between the two worlds (m, at least MIN_GAP); 0 for automatic (caveGap). */
  gap: number;
}

export interface PlacedLink {
  from: number;
  to: number;
  /** From `from`'s centre toward `to`'s. */
  axis: [number, number, number];
  gap: number;
  /** True if the gap had to grow for the new world to fit among the others. */
  grown: boolean;
  tunnel: SpiralOptions;
  phaseB: number;
  /** Plateau heights of the two mouths (m above sea level). */
  heights: [number, number];
  /** The mouths' directions from their worlds' centres. */
  mouths: [Vector3, Vector3];
}

export interface Placement {
  centers: Vector3[];
  links: PlacedLink[];
  /** Where a world on its own starts: a direction from its centre, and the ground's height there. */
  spawn: { dir: Vector3; height: number };
}

/** Directions tried for each cave, and for the start of a lone world. */
const AXES = 192;
const SPAWN_DIRS = 256;
/** Ground sampled with octaves finer than a few of these metres left out. */
const SITE_TEXEL = 4;
/** Clearance (m) between a cave and a world it doesn't join, and between two caves. */
const CAVE_CLEAR = 80;
/** Rock (m) between two worlds not joined by a cave. */
const WORLD_CLEAR = 200;

/** The spiral a cave's mouths are placed by: tighter between small worlds. */
export function tunnelFor(ra: number, rb: number, phase: number): SpiralOptions {
  return Math.max(ra, rb) < 2000
    ? { tubeRadius: 3.5, helixRadius: 14, pitchDeg: 24, depth: 6, phase }
    : { tubeRadius: 4, helixRadius: 22, pitchDeg: 24, depth: 10, phase };
}

/** The least angle between two cave mouths in a world of radius R: their plateaus stay apart. */
export function mouthSpacing(radius: number): number {
  const { flat, blend } = plateauSize(radius);
  return Math.max((25 * Math.PI) / 180, (2 * (flat + blend) + 60) / radius);
}

/**
 * Place the worlds: world `root` at `center0`, then each link's `to` world along the best
 * direction found from its `from` world. Links must be in the order the worlds are reached (a
 * tree from the root, each `to` new).
 */
export function placeUniverse(
  worlds: PlaceWorld[],
  links: PlaceLink[],
  center0: Vector3,
  seed: number,
  root = 0,
): Placement {
  const heights = worlds.map((w) => new TerrainHeights(w.terrain, w.seed, w.radius));
  const centers: (Vector3 | null)[] = worlds.map((_, i) => (i === root ? center0.clone() : null));
  const mouthDirs: Vector3[][] = worlds.map(() => []);
  const caves: { a: Vector3; b: Vector3 }[] = [];
  const placed: PlacedLink[] = [];

  links.forEach((l, li) => {
    const ca = centers[l.from];
    if (!ca || centers[l.to])
      throw new Error(`custom universe: link ${li} must join a placed world to a new one`);
    const [A, B] = [worlds[l.from], worlds[l.to]];
    const rnd = mulberry32(hashSeed(seed, li, 31));
    const axes = fibonacciSphere(AXES, rnd);
    let gap = l.gap > 0 ? Math.max(l.gap, MIN_GAP) : caveGap(A.radius, B.radius, hashSeed(seed, li, 17));
    const wanted = gap;
    let best: { d: Vector3; score: number } | null = null;
    // Grow the gap until the new world fits among the others (a small hub with big neighbours).
    for (let tries = 0; tries < 12 && !best; tries++, gap *= 1.4) {
      for (const d of axes) {
        const cb = ca.clone().addScaledVector(d, A.radius + gap + B.radius);
        if (!fits(cb, B.radius, l.from, centers, worlds)) continue;
        // The cave, wall to wall: clear of the other worlds and of the caves made before.
        const pa = ca.clone().addScaledVector(d, A.radius);
        const pb = ca.clone().addScaledVector(d, A.radius + gap);
        if (!caveClear(pa, pb, l.from, centers, worlds, caves)) continue;
        if (caves.some((c) => segmentPointDistance(c.a, c.b, cb) < B.radius + CAVE_CLEAR)) continue;
        // Far enough round from the other mouths in `from` (a soft rule: a full hub bends it).
        const spacing = mouthSpacing(A.radius);
        let crowding = 0;
        for (const m of mouthDirs[l.from]) crowding += Math.max(0, 1 - m.angleTo(d) / spacing);
        const score =
          site(heights[l.from], A, d).score +
          site(heights[l.to], B, d.clone().negate()).score -
          10 * crowding;
        if (!best || score > best.score) best = { d, score };
      }
    }
    if (!best) throw new Error(`custom universe: no room for world ${l.to}`);
    const d = best.d;
    gap /= 1.4;
    const cb = ca.clone().addScaledVector(d, A.radius + gap + B.radius);
    centers[l.to] = cb;
    caves.push({ a: ca.clone().addScaledVector(d, A.radius), b: cb.clone().addScaledVector(d, -B.radius) });

    // Where the mouths really open (round the axis, off it by the spiral's radius), and the
    // ground there: measure, place again at that height, measure again.
    const tunnel = tunnelFor(A.radius, B.radius, rnd() * Math.PI * 2);
    const phaseB = rnd() * Math.PI * 2;
    let h: [number, number] = [
      site(heights[l.from], A, d).height,
      site(heights[l.to], B, d.clone().negate()).height,
    ];
    let dirs: [Vector3, Vector3] = [d.clone(), d.clone().negate()];
    for (let pass = 0; pass < 2; pass++) {
      dirs = mouthDirections(ca, cb, A.radius, B.radius, h, tunnel, phaseB);
      h = [site(heights[l.from], A, dirs[0]).height, site(heights[l.to], B, dirs[1]).height];
    }
    mouthDirs[l.from].push(dirs[0]);
    mouthDirs[l.to].push(dirs[1]);
    placed.push({
      from: l.from,
      to: l.to,
      axis: [d.x, d.y, d.z],
      gap,
      grown: gap > wanted + 1e-6,
      tunnel,
      phaseB,
      heights: h,
      mouths: dirs,
    });
  });

  // A lone world starts at its best site.
  const home = worlds[root];
  let spawn = { dir: new Vector3(0, -1, 0), height: 1, score: Number.NEGATIVE_INFINITY };
  for (const d of fibonacciSphere(SPAWN_DIRS, mulberry32(hashSeed(seed, 99)))) {
    const s = site(heights[root], home, d);
    if (s.score > spawn.score) spawn = { dir: d, height: s.height, score: s.score };
  }
  if (centers.some((c) => !c)) throw new Error('custom universe: every world needs a cave to the others');
  return { centers: centers as Vector3[], links: placed, spawn: { dir: spawn.dir, height: spawn.height } };
}

/**
 * True if a world of radius `r` at `c` keeps WORLD_CLEAR of rock from every placed world but
 * the one its cave comes from. (Each world's pull fades out before its nearest neighbour's
 * wall, whichever that is: see CaveWorld.)
 */
function fits(
  c: Vector3,
  r: number,
  from: number,
  centers: (Vector3 | null)[],
  worlds: PlaceWorld[],
): boolean {
  return centers.every(
    (cj, j) => !cj || j === from || c.distanceTo(cj) >= r + worlds[j].radius + WORLD_CLEAR,
  );
}

function caveClear(
  a: Vector3,
  b: Vector3,
  from: number,
  centers: (Vector3 | null)[],
  worlds: PlaceWorld[],
  caves: { a: Vector3; b: Vector3 }[],
): boolean {
  for (let j = 0; j < centers.length; j++) {
    const cj = centers[j];
    if (cj && j !== from && segmentPointDistance(a, b, cj) < worlds[j].radius + CAVE_CLEAR) return false;
  }
  return caves.every((c) => segmentDistance(a, b, c.a, c.b) >= CAVE_CLEAR);
}

const _e1 = new Vector3();
const _e2 = new Vector3();
const _p = new Vector3();
const _g: GroundSample = { height: 0, slope: 0 };
/** Sample rings round a mouth: radius as a share of the plateau (flat = 1, flat + blend = 2), and count. */
const RINGS: [number, number][] = [
  [0, 1],
  [0.5, 6],
  [1, 8],
  [1.5, 10],
  [2, 12],
];

/**
 * How good a place `dir` is for a cave mouth (or the start): dry, with ground near the
 * plateau's height all round (so the blend back into the land stays gentle), not steep, not
 * high up. Also the plateau height it would get: the middle of the ground under it, kept dry.
 */
export function site(t: TerrainHeights, w: PlaceWorld, dir: Vector3): { score: number; height: number } {
  const { flat, blend } = plateauSize(w.radius);
  const dry = w.dry(dir);
  perpendicularBasis(dir, _e1, _e2);
  const inner: number[] = [];
  const outer: { h: number; r: number }[] = [];
  let wet = 0;
  let slope = 0;
  let n = 0;
  for (const [k, count] of RINGS) {
    const r = k <= 1 ? k * flat : flat + (k - 1) * blend;
    for (let i = 0; i < count; i++) {
      const a = (i / count) * Math.PI * 2 + k;
      _p.copy(dir)
        .multiplyScalar(w.radius)
        .addScaledVector(_e1, r * Math.cos(a))
        .addScaledVector(_e2, r * Math.sin(a))
        .normalize();
      t.sample(_p, SITE_TEXEL, _g);
      if (k <= 1) inner.push(_g.height);
      else outer.push({ h: _g.height, r });
      if (_g.height < dry) wet++;
      slope += _g.slope;
      n++;
    }
  }
  inner.sort((x, y) => x - y);
  const height = Math.max(inner[inner.length >> 1], dry);
  // The blend's extra steepness: the step from the plateau to the land, over the blend.
  let grade = 0;
  for (const o of outer) grade += ((1.5 * (height - o.h)) / blend) ** 2;
  grade = Math.sqrt(grade / outer.length);
  const high = (height - dry) / Math.max(50, w.terrain.ridgeHeight + w.terrain.continentHeight);
  return { score: -(6 * (wet / n) + 2 * grade + slope / n + 0.3 * high), height };
}

/** The two mouths' directions (from each world's centre), as CaveWorld will build them. */
function mouthDirections(
  ca: Vector3,
  cb: Vector3,
  ra: number,
  rb: number,
  heights: [number, number],
  tunnel: SpiralOptions,
  phaseB: number,
): [Vector3, Vector3] {
  const plateau = (c: Vector3, r: number) =>
    makeHollow({ id: '', name: '', center: [c.x, c.y, c.z], radius: r, seed: 0, sun: null });
  const ends = buildEntrances(
    plateau(ca, ra - heights[0]),
    plateau(cb, rb - heights[1]),
    [ra, rb],
    { ...tunnel, phaseB },
    [entranceOptions(ra, tunnel.tubeRadius), entranceOptions(rb, tunnel.tubeRadius)],
  );
  return [ends[0].mouth.point.clone().sub(ca).normalize(), ends[1].mouth.point.clone().sub(cb).normalize()];
}

/** `n` directions spread evenly over the sphere, turned at random. */
function fibonacciSphere(n: number, rnd: () => number): Vector3[] {
  const turn = new Vector3(rnd() - 0.5, rnd() - 0.5, rnd() - 0.5).normalize();
  const angle = rnd() * Math.PI * 2;
  const out: Vector3[] = [];
  const golden = Math.PI * (3 - Math.sqrt(5));
  for (let i = 0; i < n; i++) {
    const y = 1 - (2 * (i + 0.5)) / n;
    const r = Math.sqrt(1 - y * y);
    const phi = i * golden;
    out.push(new Vector3(r * Math.cos(phi), y, r * Math.sin(phi)).applyAxisAngle(turn, angle));
  }
  return out;
}

const _ab = new Vector3();
const _ap = new Vector3();
function segmentPointDistance(a: Vector3, b: Vector3, p: Vector3): number {
  _ab.subVectors(b, a);
  _ap.subVectors(p, a);
  const t = Math.min(Math.max(_ap.dot(_ab) / Math.max(_ab.lengthSq(), 1e-12), 0), 1);
  return _ap.addScaledVector(_ab, -t).length();
}

/** Shortest distance between two segments (sampled: caves are long and this runs rarely). */
function segmentDistance(a0: Vector3, a1: Vector3, b0: Vector3, b1: Vector3): number {
  let best = Number.POSITIVE_INFINITY;
  const p = new Vector3();
  for (let i = 0; i <= 32; i++) {
    p.lerpVectors(a0, a1, i / 32);
    best = Math.min(best, segmentPointDistance(b0, b1, p));
  }
  return best;
}
